<script setup>
import { nextTick, ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import { useToastStore } from "@/stores/toast.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";

/**
 * "Browser Sessions" section of Manage Account (GH-922) — brand re-skin of
 * the Jetstream sessions form. The password confirmation expands inline
 * instead of opening the legacy dialog modal.
 */
defineProps({
    sessions: {
        type: Array,
        default: () => [],
    },
});

const confirming = ref(false);
const passwordInput = ref(null);

const form = useForm({
    password: "",
});

async function startConfirming() {
    confirming.value = true;

    await nextTick();
    passwordInput.value?.focus();
}

function cancelConfirming() {
    confirming.value = false;
    form.reset();
    form.clearErrors();
}

const toastStore = useToastStore();

function logoutOtherBrowserSessions() {
    form.delete(route("other-browser-sessions.destroy"), {
        preserveScroll: true,
        onSuccess: () => {
            cancelConfirming();
            toastStore.success("Your other browser sessions were logged out.");
        },
        onError: () => passwordInput.value?.focus(),
        onFinish: () => form.reset(),
    });
}

const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#a79e9b] bg-white px-4 font-brand text-[15px] text-brand-navy focus:border-brand-navy focus:outline-none focus:ring-0";
</script>

<template>
    <div>
        <h2 class="font-brand text-[25px] font-bold text-[#102a3d]">
            Browser Sessions
        </h2>
        <p
            class="mt-1 max-w-[640px] font-brand text-[15px] leading-relaxed text-[#3c3938]"
        >
            Manage and log out your active sessions on other browsers and
            devices. If you feel your account has been compromised, you should
            also update your password.
        </p>

        <ul v-if="sessions.length" class="mt-8 flex flex-col gap-5">
            <li
                v-for="(session, index) in sessions"
                :key="index"
                class="flex items-center gap-4"
            >
                <svg
                    v-if="session.agent.is_desktop"
                    viewBox="0 0 24 24"
                    class="size-8 shrink-0 text-[#88959E]"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                </svg>
                <svg
                    v-else
                    viewBox="0 0 24 24"
                    class="size-8 shrink-0 text-[#88959E]"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <rect x="7" y="4" width="10" height="16" rx="1" />
                    <path d="M11 5h2M12 17v.01" />
                </svg>

                <div class="font-brand">
                    <p class="text-[15px] font-semibold text-[#102a3d]">
                        {{ session.agent.platform || "Unknown" }} &ndash;
                        {{ session.agent.browser || "Unknown" }}
                    </p>
                    <p class="text-[13px] text-[#3c3938]/80">
                        {{ session.ip_address }},
                        <span
                            v-if="session.is_current_device"
                            class="font-semibold text-[#0a8754]"
                            >This device</span
                        >
                        <span v-else
                            >Last active {{ session.last_active }}</span
                        >
                    </p>
                </div>
            </li>
        </ul>

        <div class="mt-8 max-w-[433px]">
            <BrandButton
                v-if="!confirming"
                class="h-11 px-8 text-[15px]"
                @click="startConfirming"
            >
                Log Out Other Browser Sessions
            </BrandButton>

            <div v-else>
                <p class="font-brand text-[14px] text-[#3c3938]">
                    Enter your password to confirm logging out of your other
                    browser sessions across all of your devices.
                </p>
                <input
                    ref="passwordInput"
                    v-model="form.password"
                    type="password"
                    placeholder="Password"
                    autocomplete="current-password"
                    class="mt-3"
                    :class="inputClasses"
                    @keyup.enter="logoutOtherBrowserSessions"
                />
                <p
                    v-if="form.errors.password"
                    class="mt-2 text-[13px] font-semibold text-brand-red"
                >
                    {{ form.errors.password }}
                </p>
                <div class="mt-4 flex items-center justify-end gap-6">
                    <button
                        type="button"
                        class="font-brand text-[15px] font-bold text-brand-navy underline"
                        @click="cancelConfirming"
                    >
                        Cancel
                    </button>
                    <BrandButton
                        class="h-11 px-8 text-[15px]"
                        :loading="form.processing"
                        @click="logoutOtherBrowserSessions"
                    >
                        {{ form.processing ? "Logging Out" : "Log Out" }}
                    </BrandButton>
                </div>
            </div>
        </div>
    </div>
</template>
